import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Person as PersonIcon,
  RestartAlt as RestartAltIcon,
  Schedule as ScheduleIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';

const getPrintPages = (article) => {
  if (!article) return [];

  const byImage = new Map();
  const addPage = (page, fallbackNumber) => {
    const image = typeof page === 'string' ? page : page?.image || page?.url;
    if (!image || byImage.has(image)) return;
    byImage.set(image, {
      ...(typeof page === 'string' ? {} : page),
      pageNumber: (typeof page === 'string' ? fallbackNumber : page?.pageNumber) || byImage.size + 1,
      image,
      thumbnail: typeof page === 'string' ? null : page?.thumbnail
    });
  };

  if (Array.isArray(article.pages)) {
    article.pages.forEach((page, index) => addPage(page, index + 1));
  }

  addPage({
    pageNumber: 1,
    image: article.image,
    thumbnail: article.thumbnail,
    cropArea: article.cropArea,
    markers: article.markers
  }, 1);

  if (Array.isArray(article.allImages)) {
    article.allImages.forEach((image, index) => addPage(image, index + 1));
  }

  if (Array.isArray(article.carouselMedia)) {
    article.carouselMedia
      .filter(media => media?.type === 'image')
      .forEach((media, index) => addPage(media, index + 1));
  }

  return Array.from(byImage.values()).map((page, index) => ({
    ...page,
    pageNumber: page.pageNumber || index + 1
  }));
};

const ArticlePrintFullscreenDialog = ({
  article,
  initialPageIndex,
  open,
  zoom,
  onClose,
  onZoomIn,
  onZoomOut,
  onZoomReset
}) => {
  const pages = useMemo(() => getPrintPages(article), [article]);
  const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex || 0);
  const hasMultiplePages = pages.length > 1;
  const currentPage = pages[currentPageIndex] || pages[0];
  const currentImage = currentPage?.image || article?.image;

  useEffect(() => {
    if (open) {
      setCurrentPageIndex(Math.min(initialPageIndex || 0, Math.max(pages.length - 1, 0)));
    }
  }, [initialPageIndex, open, pages.length]);

  const goToPreviousPage = () => setCurrentPageIndex(prev => Math.max(0, prev - 1));
  const goToNextPage = () => setCurrentPageIndex(prev => Math.min(pages.length - 1, prev + 1));

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'white', mb: 0.5 }}>
                {article?.siteName || (article?.mediaType === 'Revista' ? 'Revista' : 'Jornal')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {article?.date && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <ScheduleIcon sx={{ fontSize: 14 }} />
                    {new Date(article.date).toLocaleDateString('pt-BR')}
                  </Typography>
                )}
                {article?.author && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PersonIcon sx={{ fontSize: 14 }} />
                    {article.author}
                  </Typography>
                )}
                {article?.valuation > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <MoneyIcon sx={{ fontSize: 14 }} />
                    R$ {article.valuation.toLocaleString('pt-BR')}
                  </Typography>
                )}
                {hasMultiplePages && (
                  <Chip
                    label={`Página ${currentPageIndex + 1} de ${pages.length}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Diminuir zoom">
              <IconButton onClick={onZoomOut} disabled={zoom <= 25} sx={{ color: 'white' }}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>

            <Chip
              label={`${zoom}%`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'white',
                minWidth: 70
              }}
            />

            <Tooltip title="Aumentar zoom">
              <IconButton onClick={onZoomIn} disabled={zoom >= 300} sx={{ color: 'white' }}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Resetar zoom (100%)">
              <IconButton onClick={onZoomReset} sx={{ color: 'white' }}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ width: 16 }} />

            <Tooltip title="Fechar">
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 4,
            position: 'relative',
            cursor: zoom > 100 ? 'grab' : 'default',
            '&:active': {
              cursor: zoom > 100 ? 'grabbing' : 'default'
            }
          }}
        >
          {hasMultiplePages && (
            <>
              <IconButton
                onClick={goToPreviousPage}
                disabled={currentPageIndex === 0}
                sx={{
                  position: 'fixed',
                  left: 24,
                  top: '50%',
                  zIndex: 3,
                  bgcolor: 'rgba(255,255,255,0.94)',
                  '&:hover': { bgcolor: '#ffffff' }
                }}
              >
                <PrevIcon sx={{ color: '#111827', fontSize: 28 }} />
              </IconButton>
              <IconButton
                onClick={goToNextPage}
                disabled={currentPageIndex === pages.length - 1}
                sx={{
                  position: 'fixed',
                  right: 24,
                  top: '50%',
                  zIndex: 3,
                  bgcolor: 'rgba(255,255,255,0.94)',
                  '&:hover': { bgcolor: '#ffffff' }
                }}
              >
                <NextIcon sx={{ color: '#111827', fontSize: 28 }} />
              </IconButton>
            </>
          )}

          {currentImage && (
            <Box sx={{ position: 'relative', width: `${zoom}%`, maxWidth: 'none', transition: 'width 0.3s ease' }}>
              <img
                src={currentImage}
                alt={`Página ${currentPageIndex + 1} de ${article?.siteName || article?.mediaType}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  borderRadius: 4,
                  display: 'block'
                }}
              />
              {hasMultiplePages && (
                <Box sx={{
                  position: 'absolute',
                  top: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.5,
                  zIndex: 4
                }}>
                  {pages.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentPageIndex(index)}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: index === currentPageIndex
                          ? 'rgba(255,255,255,0.95)'
                          : 'rgba(255,255,255,0.45)',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 1,
            textAlign: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Use os controles acima para ajustar o zoom
          </Typography>
        </Box>
      </DialogContent>
    </PlatformDialog>
  );
};

ArticlePrintFullscreenDialog.propTypes = {
  article: PropTypes.shape({
    author: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    image: PropTypes.string,
    mediaType: PropTypes.string,
    pages: PropTypes.arrayOf(PropTypes.shape({
      image: PropTypes.string,
      pageNumber: PropTypes.number,
      thumbnail: PropTypes.string
    })),
    siteName: PropTypes.string,
    thumbnail: PropTypes.string,
    valuation: PropTypes.number
  }),
  initialPageIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onZoomReset: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  zoom: PropTypes.number.isRequired
};

ArticlePrintFullscreenDialog.defaultProps = {
  article: null,
  initialPageIndex: 0
};

export default ArticlePrintFullscreenDialog;


